import type { TileKind } from './schemas.js'

/**
 * The standard 15-tile-kind roster's movement grids, both faces, ported
 * verbatim from `~/work/OpenSCAD/warden-game.scad:519-582` (Duchess and
 * Oracle — the Enhanced tiles, scad lines 586-592 — are deliberately out of
 * scope, per `duke-moves.ts`'s own scope note). That file's header records
 * its own provenance: transcribed from the official reference-card scans of
 * two rulebook printings (base game p.12-13, 2nd-edition expansion p.15-16),
 * with every shared type spot-checked identical across both. `duke-moves.ts`
 * derives `TILE_MOVES` from this table rather than restating it, so this is
 * the single place the grids are written down.
 *
 * Grid convention (same as the scad source): 5 rows of 5 characters, row 0 is
 * the square farthest from the controlling player, `[2][2]` is normally the
 * tile itself — except where `selfRow`/`selfCol` says otherwise (Longbowman
 * sits one row back from centre on both faces).
 *   "."  empty
 *   "@"  the tile itself
 *   "m"  Move
 *   "s"  Slide
 *   "j"  Jump
 *   "S"  Jump Slide
 *   "c"  Command
 *   "x"  Strike
 */
export type TileGridChar = '.' | '@' | 'm' | 's' | 'j' | 'S' | 'c' | 'x'
export type FaceGrid = readonly [string, string, string, string, string]

export type TileDefinition = Readonly<{
  kind: TileKind
  countPerPlayer: number
  starting: FaceGrid
  'non-starting': FaceGrid
}>

// [kind, count_per_player, faceA (starting), faceB (non-starting)]
export const TILE_DEFINITIONS: readonly TileDefinition[] = [
  {
    kind: 'duke',
    countPerPlayer: 1,
    starting: ['.....', '.....', '.s@s.', '.....', '.....'],
    'non-starting': ['.....', '..s..', '..@..', '..s..', '.....'],
  },
  {
    kind: 'footman',
    countPerPlayer: 3,
    starting: ['.....', '..m..', '.m@m.', '..m..', '.....'],
    'non-starting': ['..m..', '.m.m.', '..@..', '.m.m.', '.....'],
  },
  {
    kind: 'pikeman',
    countPerPlayer: 3,
    starting: ['m...m', '.m.m.', '..@..', '.....', '.....'],
    'non-starting': ['.x.x.', '..m..', '..@..', '..m..', '..m..'],
  },
  {
    kind: 'bowman',
    countPerPlayer: 1,
    starting: ['.....', '..m..', 'jm@mj', '.....', '..j..'],
    'non-starting': ['..x..', '.xmx.', '..@..', '.m.m.', '.....'],
  },
  {
    kind: 'champion',
    countPerPlayer: 1,
    starting: ['..j..', '..m..', 'jm@mj', '..m..', '..j..'],
    'non-starting': ['..j..', '..x..', 'jx@xj', '..x..', '..j..'],
  },
  {
    kind: 'general',
    countPerPlayer: 1,
    starting: ['.j.j.', '..m..', 'm.@.m', '..m..', '.....'],
    'non-starting': ['.j.j.', '..m..', 'mm@mm', '.ccc.', '.....'],
  },
  {
    kind: 'knight',
    countPerPlayer: 1,
    // FIX (carried from warden-game.scad:546): dots one row back from @.
    starting: ['.j.j.', '.....', '.m@m.', '..m..', '..m..'],
    'non-starting': ['.....', '..s..', '..@..', '.m.m.', 'm...m'],
  },
  {
    kind: 'marshall',
    countPerPlayer: 1,
    starting: ['j...j', '.....', '.s@s.', '.....', '..j..'],
    'non-starting': ['.....', '.mmm.', 'mm@mm', '.m.m.', '.....'],
  },
  {
    kind: 'priest',
    countPerPlayer: 1,
    starting: ['.....', '.s.s.', '..@..', '.s.s.', '.....'],
    'non-starting': ['j...j', '.m.m.', '..@..', '.m.m.', 'j...j'],
  },
  {
    kind: 'seer',
    countPerPlayer: 1,
    starting: ['..j..', '.m.m.', 'j.@.j', '.m.m.', '..j..'],
    'non-starting': ['j...j', '..m..', '.m@m.', '..m..', 'j...j'],
  },
  {
    kind: 'wizard',
    countPerPlayer: 1,
    starting: ['.....', '.mmm.', '.m@m.', '.mmm.', '.....'],
    'non-starting': ['j.j.j', '.....', 'j.@.j', '.....', 'j.j.j'],
  },
  {
    kind: 'assassin',
    countPerPlayer: 1,
    starting: ['..S..', '.....', '..@..', '.....', 'S...S'],
    'non-starting': ['S...S', '.....', '..@..', '.....', '..S..'],
  },
  {
    kind: 'dragoon',
    countPerPlayer: 1,
    starting: ['x.x.x', '.....', '.m@m.', '.....', '.....'],
    'non-starting': ['.jmj.', '..m..', '..@..', '.s.s.', '.....'],
  },
  {
    kind: 'longbowman',
    countPerPlayer: 1,
    // FIX (carried from warden-game.scad:574-575): @ one row back from centre
    // on both faces.
    starting: ['.....', '.....', '..m..', '.m@m.', '..m..'],
    'non-starting': ['..x..', '..x..', '.....', '..@..', '.m.m.'],
  },
  {
    kind: 'ranger',
    countPerPlayer: 1,
    // !!! No official reference card for Ranger in either rulebook printing
    // this was transcribed from (warden-game.scad:577-582) — reconstructed,
    // not confirmed. Verify against a physical tile or a photo before relying
    // on this in a real game.
    starting: ['.j.j.', 'j.s.j', '..@..', '..s..', '.....'],
    'non-starting': ['.....', '.s.s.', '..@..', '.....', '.j.j.'],
  },
]

/** Row/col of the "@" cell in a face grid — usually [2][2], not always (see Longbowman). */
export function selfPosition(face: FaceGrid): Readonly<{ row: number; col: number }> {
  for (let row = 0; row < 5; row++) {
    const col = face[row].indexOf('@')
    if (col !== -1) return { row, col }
  }
  throw new Error('Tile face grid has no "@" cell')
}
